import { useFormikContext } from "formik";
import { useWorkspaceTaskGroups } from "@api/taskGroup.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { TaskCreate } from "@common/contracts/task.contract";
import type { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { TaskType } from "@common/types/task";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    PickerChevron,
    PickerItemData,
    PickerTriggerElement,
} from "@design-system/Picker/Picker.types";
import {
    isOrgTaskTemplatePickerValue,
    parseOrgTaskTemplateIdFromPickerValue,
} from "@components/settings/OrganizationCloseTemplates/orgTaskTemplatePicker.utils";
import { useOrgTaskTemplatesForWorkspace } from "@components/settings/OrganizationCloseTemplates/useOrgTaskTemplatesForWorkspace";
import {
    buildWorkflowPickerItems,
    type WorkflowPickerValue,
} from "@components/settings/OrganizationCloseTemplates/workflowPickerItems";
import { WORKSPACE_WORKFLOW_TASK_TYPES } from "@common/constants/taskTypeDefinitions";
import { getRequiredFeatureForTaskType } from "@common/constants/taskTypePlanRequirements";
import { useFeatureAccessChecker } from "@hooks/useAllowFeature";
import { useScope } from "@state/scopeContext.lib";
import { createActionSectionTrigger } from "./actionSectionTrigger";
import { getTitleAfterWorkflowChange } from "./statementRetrieval";
import {
    getLockedWorkflowOption,
    getWorkflowViewForType,
    isPeriodTaskType,
} from "./taskWorkflow";
import { buildWorkspaceWorkflowGroups } from "./taskWorkflowGroups";
import {
    applyOrgTaskTemplateToTaskValues,
    filterOrgTaskTemplatesForWorkspaceTask,
} from "./taskWorkflowTemplate";

interface TaskWorkflowFieldProps {
    isLocked?: boolean;
    onTemplateApplied?: () => void;
    onWorkflowChange?: (type: TaskType) => void;
    trigger?: PickerTriggerElement;
    chevron?: PickerChevron;
    triggerClassName?: string;
}

export const TaskWorkflowField = ({
    isLocked = false,
    onTemplateApplied,
    onWorkflowChange,
    trigger,
    chevron,
    triggerClassName,
}: TaskWorkflowFieldProps) => {
    const formik = useFormikContext<TaskCreate>();
    const { workspaceId } = useScope();
    const { data: members = [], isLoading: isLoadingMembers } =
        useCurrentWorkspaceMembers();
    const { data: taskGroups = [], isLoading: isLoadingTaskGroups } =
        useWorkspaceTaskGroups();
    const orgTaskTemplates = useOrgTaskTemplatesForWorkspace({
        workspaceId: workspaceId ?? null,
        basis: null,
    });

    const { hasFeatureAccess, isFetched: isBillingFetched } =
        useFeatureAccessChecker();

    const isTaskTypeAllowed = (type: TaskType): boolean => {
        const requiredFeature = getRequiredFeatureForTaskType(type);
        return (
            !isBillingFetched ||
            !requiredFeature ||
            hasFeatureAccess(requiredFeature)
        );
    };

    const isTemplateContextLoading = isLoadingMembers || isLoadingTaskGroups;
    const templates = isTemplateContextLoading
        ? []
        : filterOrgTaskTemplatesForWorkspaceTask(
              orgTaskTemplates,
              isTaskTypeAllowed,
          );
    const workflowItems = buildWorkflowPickerItems({
        groups: buildWorkspaceWorkflowGroups({ isTaskTypeAllowed }),
        orgTaskTemplates: templates,
    });

    const lockedWorkflowItems: Array<PickerItemData<WorkflowPickerValue>> = [
        getLockedWorkflowOption(formik.values.type),
    ];

    const handleTypeChange = (nextType: TaskType) => {
        if (isPeriodTaskType(nextType) && nextType === formik.values.type) {
            return;
        }
        void formik.setValues({
            ...formik.values,
            title: getTitleAfterWorkflowChange({
                title: formik.values.title,
                nextTaskType: nextType,
            }),
            type: nextType,
            sticky: undefined,
            view: getWorkflowViewForType({
                type: nextType,
                currentView: formik.values.view,
                now: new Date(),
            }),
        });
        onWorkflowChange?.(nextType);
    };

    const handleTemplateSelect = (template: OrganizationTaskTemplate) => {
        const nextValues = applyOrgTaskTemplateToTaskValues({
            template,
            values: formik.values,
            now: new Date(),
            assignableUserIds: members.map((member) => member.user.id),
            availableGroupIds: taskGroups.map((taskGroup) => taskGroup.id),
        });
        void formik.setValues(nextValues);
        onTemplateApplied?.();
        onWorkflowChange?.(nextValues.type);
    };

    const handleValueChange = (value: WorkflowPickerValue | null) => {
        if (value === null) {
            return;
        }
        if (isOrgTaskTemplatePickerValue(value)) {
            const templateId = parseOrgTaskTemplateIdFromPickerValue(value);
            const template = templates.find(({ id }) => id === templateId);
            if (template) {
                handleTemplateSelect(template);
            }
            return;
        }
        const nextType = WORKSPACE_WORKFLOW_TASK_TYPES.find(
            (workflowType) => workflowType === value,
        );
        if (nextType) {
            handleTypeChange(nextType);
        }
    };

    return (
        <Picker.Select
            items={isLocked ? lockedWorkflowItems : workflowItems}
            nested="flyout"
            disabled={isLocked}
            value={formik.values.type}
            onValueChange={handleValueChange}
            chevron={chevron}
            trigger={trigger ?? createActionSectionTrigger(triggerClassName)}
        />
    );
};
